import { useCallback, useEffect, useState } from "react";

export interface EditorSettings {
  model: string;
  provider: "anthropic" | "openai" | "local";
  localBaseUrl: string;
  warnThresholdCents: number;
  hardCapEnabled: boolean;
  hardCapCents: number;
  promptCachingEnabled: boolean;
  preflightEstimateEnabled: boolean;
  spentSessionCents: number;
}

export const DEFAULT_EDITOR_SETTINGS: EditorSettings = {
  model: "claude-haiku-4-5-20251001",
  provider: "anthropic",
  localBaseUrl: "http://localhost:11434/v1",
  warnThresholdCents: 100,
  hardCapEnabled: false,
  hardCapCents: 500,
  promptCachingEnabled: true,
  preflightEstimateEnabled: false,
  spentSessionCents: 0,
};

const KEYS: Record<keyof EditorSettings, string> = {
  model: "agentguard-editor-model",
  provider: "agentguard-editor-provider",
  localBaseUrl: "agentguard-editor-local-url",
  warnThresholdCents: "agentguard-editor-warn-cents",
  hardCapEnabled: "agentguard-editor-cap-enabled",
  hardCapCents: "agentguard-editor-cap-cents",
  promptCachingEnabled: "agentguard-editor-cache-on",
  preflightEstimateEnabled: "agentguard-editor-preflight-on",
  spentSessionCents: "agentguard-editor-spent-cents",
};

function read(): EditorSettings {
  const out = { ...DEFAULT_EDITOR_SETTINGS };
  for (const k of Object.keys(KEYS) as (keyof EditorSettings)[]) {
    const raw = localStorage.getItem(KEYS[k]);
    if (raw === null) continue;
    const def = DEFAULT_EDITOR_SETTINGS[k];
    if (typeof def === "boolean") (out as Record<string, unknown>)[k] = raw === "true";
    else if (typeof def === "number") {
      // Fall back to the default on a non-numeric value — NaN would silently
      // corrupt cost controls (e.g. hardCapCents=NaN makes `spent >= cap`
      // always false, disabling the spend hard-cap).
      const n = parseFloat(raw);
      (out as Record<string, unknown>)[k] = Number.isNaN(n) ? def : n;
    } else (out as Record<string, unknown>)[k] = raw;
  }
  return out;
}

function write(patch: Partial<EditorSettings>) {
  for (const [k, v] of Object.entries(patch)) {
    localStorage.setItem(KEYS[k as keyof EditorSettings], String(v));
  }
}

export function useEditorSettings() {
  const [settings, setSettings] = useState<EditorSettings>(() => read());
  useEffect(() => {
    const onStorage = () => setSettings(read());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const update = useCallback((patch: Partial<EditorSettings>) => {
    write(patch);
    setSettings((s) => ({ ...s, ...patch }));
  }, []);

  const addSpend = useCallback((cents: number) => {
    setSettings((s) => {
      const next = s.spentSessionCents + cents;
      write({ spentSessionCents: next });
      return { ...s, spentSessionCents: next };
    });
  }, []);

  const resetMeter = useCallback(() => {
    write({ spentSessionCents: 0 });
    setSettings((s) => ({ ...s, spentSessionCents: 0 }));
  }, []);

  const hardCapReached =
    settings.hardCapEnabled && settings.spentSessionCents >= settings.hardCapCents;

  return { settings, update, addSpend, resetMeter, hardCapReached };
}
